import type { GenericArray } from '@Types';
import type { MedicalCostType, PaymentMethod } from './types';
import { INTAKE_TYPES } from '@IntakeModule';
import { MEDICAL_COST_TYPES } from './enums';
import CartRoutes from './routes';
import { OUTCOMES_TYPES, PAYMENT_METHODS } from './types';

const { CARD_READER, CASH, CHECK, CREDIT, OTHER, SPLIT_PAYMENT } = PAYMENT_METHODS;

const SANITIZED_OUTCOME_TYPES: GenericArray = {
  adopted: 'cart.outcomeOptions.adopted',
  clinic_out: 'cart.outcomeOptions.clinic_out',
  died_in_care: 'cart.outcomeOptions.died_in_care',
  doa: 'cart.outcomeOptions.doa',
  finder_kept: 'cart.outcomeOptions.finderKept',
  lost: 'cart.outcomeOptions.lost',
  owner_requested_euthanasia: 'cart.outcomeOptions.owner_requested_euthanasia',
  rehome: 'cart.outcomeOptions.rehome',
  rehome_shelter_intake: 'cart.outcomeOptions.rehome_shelter_intake',
  rehome_unknown: 'cart.outcomeOptions.rehome_unknown',
  rehome_deceased: 'cart.outcomeOptions.rehome_deceased',
  rehomed: 'cart.outcomeOptions.rehomed',
  remained_with_finder: 'cart.outcomeOptions.remained_with_finder',
  remained_with_owner: 'cart.outcomeOptions.remained_with_owner',
  return_to_field: 'cart.outcomeOptions.return_to_field',
  wildlife_out: 'cart.outcomeOptions.wildlife_out',
  return_to_owner: 'cart.outcomeOptions.return_to_owner',
  reunited_with_owner: 'cart.outcomeOptions.reunited_with_owner',
  lost_found_returned_to_field: 'cart.outcomeOptions.lost_found_returned_to_field',
  lost_found_deceased: 'cart.outcomeOptions.lost_found_deceased',
  service_out: 'cart.outcomeOptions.service_out',
  shelter_euthanasia: 'cart.outcomeOptions.shelter_euthanasia',
  shelter_intake: 'cart.outcomeOptions.shelter_intake',
  transfer_out: 'cart.outcomeOptions.transfer_out',
  unknown: 'cart.outcomeOptions.unknown',
};

const SANITIZED_INTAKE_TYPES: GenericArray = {
  [INTAKE_TYPES.ADD_PET]: 'cart.intakeOptions.add_pet',
  [INTAKE_TYPES.ADD_TRANSPORT_ANIMAL]: 'cart.intakeOptions.add_transport_animal',
  [INTAKE_TYPES.BOARDING_IN]: 'cart.intakeOptions.boarding_in',
  [INTAKE_TYPES.BORN_IN_CARE]: 'cart.intakeOptions.born_in_care',
  [INTAKE_TYPES.CLINIC_IN]: 'cart.intakeOptions.clinic_in',
  [INTAKE_TYPES.DOA]: 'cart.intakeOptions.doa',
  [INTAKE_TYPES.FOUND]: 'cart.intakeOptions.found',
  [INTAKE_TYPES.LOST]: 'cart.intakeOptions.lost',
  [INTAKE_TYPES.OWNER_REQUESTED_EUTHANASIA]: 'cart.intakeOptions.owner_requested_euthanasia',
  [INTAKE_TYPES.OWNER_SURRENDER]: 'cart.intakeOptions.owner_surrender',
  [INTAKE_TYPES.RETURN]: 'cart.intakeOptions.return',
  [INTAKE_TYPES.SEIZED]: 'cart.intakeOptions.seized',
  [INTAKE_TYPES.SERVICE_IN]: 'cart.intakeOptions.service_in',
  [INTAKE_TYPES.STRAY]: 'cart.intakeOptions.stray',
  [INTAKE_TYPES.TRANSFER_IN]: 'cart.intakeOptions.transfer_in',
  [INTAKE_TYPES.UNKNOWN]: 'cart.intakeOptions.unknown',
  [INTAKE_TYPES.WILDLIFE_IN]: 'cart.intakeOptions.wildlife_in',
};

const DONATION_ROUTES = {
  completed: '/shelter/donation/payment-completed/',
  pending: '/shelter/donation/payment-pending/',
};

const INTAKE_ROUTES = {
  completed: '/shelter/animal/intake-payment-completed/',
  pending: '/shelter/animal/intake-payment-pending/',
};

const OUTCOME_ROUTES = {
  completed: '/shelter/animal/outcome-completed/',
  pending: '/shelter/animal/outcome-pending/',
};

const CHECK_IN_INTAKE_TYPES = [INTAKE_TYPES.BOARDING_IN, INTAKE_TYPES.CLINIC_IN, INTAKE_TYPES.SERVICE_IN];

const INTAKES_WITH_PAYMENT = [
  INTAKE_TYPES.BOARDING_IN,
  INTAKE_TYPES.CLINIC_IN,
  INTAKE_TYPES.DOA,
  INTAKE_TYPES.OWNER_REQUESTED_EUTHANASIA,
  INTAKE_TYPES.OWNER_SURRENDER,
  INTAKE_TYPES.RETURN,
  INTAKE_TYPES.SEIZED,
  INTAKE_TYPES.SERVICE_IN,
  INTAKE_TYPES.STRAY,
  INTAKE_TYPES.TRANSFER_IN,
  INTAKE_TYPES.WILDLIFE_IN,
];
const OUTCOMES_WITH_CART = [
  OUTCOMES_TYPES.ADOPTED,
  OUTCOMES_TYPES.CLINIC_OUT,
  OUTCOMES_TYPES.RETURN_TO_OWNER,
  OUTCOMES_TYPES.SERVICE_OUT,
];
const OUTCOMES_WITH_CONTRACT = ['adopted', 'return_to_owner', 'transfer_out', 'service_out', 'clinic_out'];

const PAYMENT_OPTIONS = <PaymentMethod[]>[
  {
    icon: 'faCreditCard',
    key: 'credit_card',
    label: 'cart.paymentOptions.creditCard',
    value: CREDIT,
  },
  {
    icon: 'faDollarSign',
    key: 'cash',
    label: 'cart.paymentOptions.cash',
    value: CASH,
  },
  {
    icon: 'faCheck',
    key: 'check',
    label: 'cart.paymentOptions.check',
    value: CHECK,
  },
  {
    icon: 'faSdCard',
    key: 'card_reader',
    label: 'cart.paymentOptions.cardReader',
    value: CARD_READER,
  },
  {
    icon: 'faCashRegister',
    key: 'other',
    label: 'cart.paymentOptions.other',
    value: OTHER,
  },
  {
    icon: 'faMoneyCheckDollar',
    key: 'split_payment',
    label: 'cart.paymentOptions.splitPayment',
    value: SPLIT_PAYMENT,
  },
];

const MEDICAL_COLLECTIONS: MedicalCostType[] = [
  MEDICAL_COST_TYPES.MEDICATIONS,
  MEDICAL_COST_TYPES.VACCINATIONS,
  MEDICAL_COST_TYPES.PROCEDURES,
  MEDICAL_COST_TYPES.DIAGNOSTICS,
  MEDICAL_COST_TYPES.SURGERIES,
  MEDICAL_COST_TYPES.EXAMS,
];

const ACTIVE_CART_ROUTES = CartRoutes.filter((route) => route.meta?.checkoutStatus === 'active').map(
  ({ name }) => name
);

export {
  ACTIVE_CART_ROUTES,
  CHECK_IN_INTAKE_TYPES,
  DONATION_ROUTES,
  OUTCOMES_WITH_CONTRACT,
  INTAKES_WITH_PAYMENT,
  INTAKE_ROUTES,
  MEDICAL_COLLECTIONS,
  OUTCOMES_WITH_CART,
  OUTCOME_ROUTES,
  PAYMENT_OPTIONS,
  SANITIZED_INTAKE_TYPES,
  SANITIZED_OUTCOME_TYPES,
};
